import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { useParams, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { financeStatementsColumns } from "./financeStatements.columns";
import {
  gridFilteredSortedRowIdsSelector,
  gridVisibleColumnFieldsSelector,
} from "@mui/x-data-grid-pro";

const FinanceStatementsDataGrid = forwardRef(({ searchText }, ref) => {
  const { companyId } = useParams();
  const location = useLocation();
  const apiRef = useGridApiRef();
  
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Extract preserved state passed from Portfolio Table
  const dynamicDealId = location.state?.deal_id;

  /* ---------------- FORMATTING UTILITY ---------------- */
  /**
   * Safely converts backend values to strings with 2 decimal points.
   * Handles commas in strings and null/NA values.
   */
  const formatToTwoDecimals = (val) => {
    if (val === null || val === undefined || val === "NA" || val === "") return "—";
    
    // Clean string and parse to float
    const cleanStr = String(val).replace(/,/g, "");
    const num = parseFloat(cleanStr);
    
    return isNaN(num) ? "—" : num.toFixed(2);
  };

  useEffect(() => {
    const fetchFinanceStatements = async () => {
      // Ensure we have a dealId to fetch specific company statements
      if (!dynamicDealId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4200/api';
        
        // Fetch statement history based on dealId
        const url = `${API_BASE_URL}/portfolio/financial-statements?dealId=${dynamicDealId}&partnerCode=${location.state?.partner_doc}&company=${location.state?.company_name}`;
        const response = await fetch(url);
        const result = await response.json();

        if (result.status === "success" && result.data) {
          const transformedRows = result.data.map((item, index) => {
            // Apply 2-decimal formatting to all financial metrics
            const arrVal = formatToTwoDecimals(item.ARR);
            const cashVal = formatToTwoDecimals(item.Cash_Balance);
            const revenueVal = formatToTwoDecimals(item.Revenue);
            const ebitdaVal = formatToTwoDecimals(item.EBITDA);
            const marginVal = formatToTwoDecimals(item.Gross_Margin);

            return {
              id: index, // Unique ID for DataGrid
              year: item.year || "—",
              quarter: item.quarter ? `Q${item.quarter}` : "—",
              reportingCurrency: item.Reporting_Currency || "—",
              // Map to keys expected by financeStatementsColumns
              arr: arrVal !== "—" ? `${arrVal}M` : "—",
              runway: item.Runway || "—",
              cashBalance: cashVal !== "—" ? `${cashVal}M` : "—",
              revenue: revenueVal !== "—" ? `${revenueVal}M` : "—",
              ebitda: ebitdaVal !== "—" ? `${ebitdaVal}M` : "—",
              grossMargin: marginVal !== "—" ? `${marginVal}%` : "—",
            };
          });
          setRows(transformedRows);
        }
      } catch (error) {
        console.error("Error fetching Financial Reports:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFinanceStatements();
  }, [dynamicDealId]);

  /* ---------------- SEARCH ---------------- */
  const filteredRows = useMemo(() => {
    if (!searchText) return rows;
    const q = searchText.toLowerCase().trim();
    return rows.filter((row) =>
      Object.values(row).some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [rows, searchText]);

  /* ---------------- EXCEL EXPORT (VISIBLE ONLY) ---------------- */
  const exportToExcel = () => {
    if (!apiRef.current) return;

    const rowIds = gridFilteredSortedRowIdsSelector(apiRef);
    if (!rowIds.length) return;

    const visibleFields = gridVisibleColumnFieldsSelector(apiRef);

    const exportData = rowIds.map((id) => {
      const row = apiRef.current.getRow(id);
      const obj = {};
      visibleFields.forEach((field) => {
        // Map field keys to human-readable headers from your column definitions
        const column = financeStatementsColumns.find(col => col.field === field);
        const headerName = column ? column.headerName : field;
        obj[headerName] = row[field];
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Financial Reports");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), 
      `Finance_Statements_${companyId}.xlsx`);
  };

  /* ---------------- EXPOSE METHODS TO PARENT ---------------- */
  useImperativeHandle(ref, () => ({
    openColumnsPanel: () => apiRef.current?.showPreferences("columns"),
    openFiltersPanel: () => apiRef.current?.showPreferences("filters"),
    exportToExcel,
  }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={420}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 420, width: "100%", bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2 }}>
      <DataGridPro
        apiRef={apiRef}
        rows={filteredRows}
        columns={financeStatementsColumns}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: "year", sort: "desc" }],
          },
        }}
        sx={{
          "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f9fafb" },
        }}
      />
    </Box>
  );
});

export default FinanceStatementsDataGrid;